import React, { useEffect, useState } from "react";
import Select from "react-select";
import useFetch from "hooks/useFetch";
import {
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  TextField,
  Textarea,
  useConfirm,
  useToast,
} from "stories/Admin";
import { adminSelectStyles } from "../UsersPage/adminSelectStyles";
import PageModal from "./PageModal";
import styles from "./LocationsPage.module.scss";

interface Location {
  id: number;
  name: string;
  lat: number;
  lon: number;
  county: string;
  municipality: string;
  cityId?: number;
  prayerTimesTables: {
    id: number;
    name: string;
  }[];
}

interface PrayerTimeTable {
  id: number;
  name: string;
}

const LocationsPage: React.FC = () => {
  const { execute } = useFetch();
  const toast = useToast();
  const confirm = useConfirm();
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showModal, setShowModal] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [prayerTimeTables, setPrayerTimeTables] = useState<PrayerTimeTable[]>(
    [],
  );
  const [selectedPrayerTable, setSelectedPrayerTable] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [filteredPrayerTimeOptions, setFilteredPrayerTimeOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: "",
    lat: 0,
    lon: 0,
    districtCode: "",
    county: "",
    municipality: "",
    information: "",
  });
  const [showCreatePrayerTableModal, setShowCreatePrayerTableModal] =
    useState(false);
  const [newPrayerTableName, setNewPrayerTableName] = useState("");
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [locationBeingEdited, setLocationBeingEdited] =
    useState<Location | null>(null);
  const [editLat, setEditLat] = useState<number | "">("");
  const [editLon, setEditLon] = useState<number | "">("");

  const fetchLocations = async () => {
    try {
      const response = (await execute(
        "/api/v1/locations",
        "GET",
      )) as Location[];
      if (response) setLocations(response);
    } catch (error) {
      console.error("Feil ved henting av lokasjoner:", error);
    }
  };

  const fetchPrayerTimeTables = async (locationId: number) => {
    try {
      const response = (await execute(
        "/api/v1/prayer-times-tables",
        "GET",
      )) as PrayerTimeTable[];
      if (response) {
        const assignedTableIds = new Set(
          locations
            .find((loc) => loc.id === locationId)
            ?.prayerTimesTables.map((table) => table.id) || [],
        );
        setPrayerTimeTables(response);

        const options = response
          .filter((table) => !assignedTableIds.has(table.id))
          .map((table) => ({
            value: table.id,
            label: table.name,
          }));

        setFilteredPrayerTimeOptions(options);
      }
    } catch (error) {
      console.error("Feil ved henting av bønnetidstabeller:", error);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filtered = locations.filter((location) =>
        location.name.toLowerCase().includes(lowerSearchTerm),
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations([]);
    }
    setCurrentPage(1);
  }, [searchTerm, locations]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLocations.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleAddPrayerTimeTable = (locationId: number) => {
    setSelectedLocationId(locationId);
    fetchPrayerTimeTables(locationId);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedLocationId(null);
    setSelectedPrayerTable(null);
  };

  const handleModalSubmit = async () => {
    if (selectedLocationId === null || selectedPrayerTable === null) {
      toast.error("Velg en bønnetabell.");
      return;
    }

    const payload = {
      locationId: selectedLocationId,
      prayerTimeTableId: selectedPrayerTable.value,
    };

    try {
      await execute(
        "/api/v1/prayer-times-tables-with-location",
        "POST",
        payload,
      );
      toast.success("Bønnetabellen er lagt til");
      fetchLocations();
      handleModalClose();
    } catch (error) {
      console.error("Feil ved oppretting av bønnetidstabell:", error);
      toast.error("Kunne ikke legge til bønnetabellen.");
    }
  };

  const handleShowDeleteModal = async (
    locationId: number,
    prayerTableId: number,
    prayerTableName: string,
  ) => {
    setSelectedPrayerTable({ value: prayerTableId, label: prayerTableName });
    const ok = await confirm({
      title: "Fjern bønnetabell?",
      message: (
        <>
          Tabellen <b>{prayerTableName}</b> fjernes fra lokasjonen.
        </>
      ),
      confirmLabel: "Fjern",
      tone: "danger",
    });
    if (ok) {
      await handleDeleteConfirm(locationId, {
        value: prayerTableId,
        label: prayerTableName,
      });
    } else {
      setSelectedPrayerTable(null);
    }
  };

  const handleDeleteConfirm = async (
    selectedLocationDeleteId: number | null,
    selectedPrayerTable: { value: number; label: string } | null,
  ) => {
    if (selectedLocationDeleteId === null || !selectedPrayerTable) return;

    try {
      await execute(
        `/api/v1/prayer-times-tables-with-location/${selectedLocationDeleteId}/${selectedPrayerTable.value}`,
        "DELETE",
      );
      toast.success("Bønnetabellen er fjernet");
      fetchLocations();
      setSelectedPrayerTable(null);
    } catch (error) {
      console.error("Feil ved sletting av bønnetidstabell:", error);
      toast.error("Kunne ikke fjerne bønnetabellen.");
    }
  };

  const handleCreateLocationModalClose = () => {
    setShowCreateLocationModal(false);
    setNewLocation({
      name: "",
      lat: 0,
      lon: 0,
      districtCode: "",
      county: "",
      municipality: "",
      information: "",
    });
  };

  const handleCreateLocation = async () => {
    try {
      await execute("/api/v1/locations", "POST", newLocation);
      toast.success("Lokasjonen er opprettet");
      fetchLocations();
      handleCreateLocationModalClose();
    } catch (error) {
      console.error("Feil ved oppretting av ny lokasjon:", error);
      toast.error("Kunne ikke opprette lokasjonen.");
    }
  };

  const handleCreatePrayerTimeTable = async () => {
    if (!newPrayerTableName.trim()) {
      toast.error("Skriv inn et navn på bønnetabellen.");
      return;
    }

    const payload = { name: newPrayerTableName };

    try {
      await execute("/api/v1/prayer-times-tables", "POST", payload);
      toast.success("Bønnetabellen er opprettet");
      setShowCreatePrayerTableModal(false);
      setNewPrayerTableName("");
      fetchPrayerTimeTables(selectedLocationId || 0);
    } catch (error) {
      console.error("Feil ved oppretting av bønnetidstabell:", error);
      toast.error("Kunne ikke opprette bønnetabellen.");
    }
  };

  const openEditLocationModal = (location: Location) => {
    setLocationBeingEdited(location);
    setEditLat(location.lat ?? "");
    setEditLon(location.lon ?? "");
    setShowEditLocationModal(true);
  };

  const closeEditLocationModal = () => {
    setShowEditLocationModal(false);
    setLocationBeingEdited(null);
  };

  const handleSaveLocationEdits = async () => {
    if (!locationBeingEdited) return;

    if (editLat === "" || editLon === "") {
      toast.error("Breddegrad og lengdegrad må fylles ut.");
      return;
    }

    const updatedLocation = {
      lat: Number(editLat),
      lon: Number(editLon),
      cityId: locationBeingEdited.cityId,
    };

    try {
      await execute(
        `/api/v1/locations/${locationBeingEdited.id}`,
        "PATCH",
        updatedLocation,
      );

      const updatedLocationFull: Location = {
        ...locationBeingEdited,
        lat: Number(editLat),
        lon: Number(editLon),
      };

      setLocations((prev) =>
        prev.map((loc) =>
          loc.id === updatedLocationFull.id ? updatedLocationFull : loc,
        ),
      );
      setFilteredLocations((prev) =>
        prev.map((loc) =>
          loc.id === updatedLocationFull.id ? updatedLocationFull : loc,
        ),
      );
      toast.success("Koordinatene er oppdatert");
      closeEditLocationModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Feil ved oppdatering av lokasjon:", error);
      toast.error("Kunne ikke oppdatere koordinatene.");
    }
  };

  const pageCount = Math.ceil(filteredLocations.length / itemsPerPage);

  return (
    <>
      <PageHeader
        title="Lokasjoner og tabeller"
        subtitle="Byer med koordinater, og hvilke bønnetabeller hver by har."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="table" size={16} />}
              onClick={() => setShowCreatePrayerTableModal(true)}
            >
              Ny bønnetabell
            </Button>
            <Button
              icon={<Icon name="plus" size={16} />}
              onClick={() => setShowCreateLocationModal(true)}
            >
              Ny lokasjon
            </Button>
          </>
        }
      />

      <div className={styles.filters}>
        <TextField
          aria-label="Søk etter lokasjon"
          type="search"
          placeholder="Søk etter lokasjon …"
          className={styles.search}
          fieldClassName={styles.searchField}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <span className={styles.muted}>{filteredLocations.length} treff</span>
        )}
      </div>

      {currentItems.length > 0 ? (
        <div className={styles.grid}>
          {currentItems.map((location) => (
            <Card key={location.id} className={styles.locationCard}>
              <div className={styles.locationHead}>
                <span className={styles.pinIcon} aria-hidden="true">
                  <Icon name="pin" size={20} />
                </span>
                <span className={styles.locationText}>
                  <b className={styles.locationName}>{location.name}</b>
                  <span className={styles.muted}>
                    {location.county} fylke · {location.municipality} kommune ·{" "}
                    <span className={styles.num}>
                      {location.lat}, {location.lon}
                    </span>
                  </span>
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Icon name="edit" size={14} />}
                  onClick={() => openEditLocationModal(location)}
                >
                  Rediger koordinater
                </Button>
              </div>
              <div className={styles.tables}>
                <span className={styles.tablesLabel}>Bønnetabeller:</span>
                {location.prayerTimesTables.length > 0 ? (
                  location.prayerTimesTables.map((table) => (
                    <span key={table.id} className={styles.chip}>
                      {table.name} (#{table.id})
                      <button
                        type="button"
                        className={styles.chipRemove}
                        aria-label={`Fjern ${table.name} (#${table.id})`}
                        title="Fjern"
                        onClick={() =>
                          handleShowDeleteModal(
                            location.id,
                            table.id,
                            table.name,
                          )
                        }
                      >
                        <Icon name="x" size={12} strokeWidth={2.4} />
                      </button>
                    </span>
                  ))
                ) : (
                  <span className={styles.muted}>Ingen tabeller</span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Icon name="plus" size={14} />}
                  onClick={() => handleAddPrayerTimeTable(location.id)}
                >
                  Legg til tabell
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : searchTerm ? (
        <Card>
          <EmptyState icon="pin" title="Ingen lokasjoner funnet">
            Prøv et annet søk.
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <EmptyState icon="search" title="Søk etter en lokasjon">
            Skriv navnet på en by for å se koordinater og bønnetabeller.
          </EmptyState>
        </Card>
      )}

      {filteredLocations.length > itemsPerPage && (
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          onChange={paginate}
        />
      )}

      <PageModal
        open={showModal}
        title="Legg til bønnetabell"
        icon="table"
        onClose={handleModalClose}
        footer={
          <>
            <Button variant="secondary" onClick={handleModalClose}>
              Avbryt
            </Button>
            <Button
              icon={<Icon name="plus" size={16} />}
              onClick={handleModalSubmit}
              disabled={!selectedPrayerTable}
            >
              Legg til
            </Button>
          </>
        }
      >
        <div className={styles.field}>
          <label className={styles.label} htmlFor="prayerTimeTableSelect">
            Velg bønnetabell
          </label>
          <Select
            inputId="prayerTimeTableSelect"
            options={filteredPrayerTimeOptions}
            onChange={(option) => setSelectedPrayerTable(option)}
            placeholder="Velg en bønnetabell"
            isClearable
            styles={adminSelectStyles<{ value: number; label: string }>()}
            menuPortalTarget={document.body}
            noOptionsMessage={() => "Ingen bønnetabeller tilgjengelige"}
          />
        </div>
      </PageModal>

      <PageModal
        open={showCreateLocationModal}
        title="Ny lokasjon"
        icon="pin"
        onClose={handleCreateLocationModalClose}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={handleCreateLocationModalClose}
            >
              Avbryt
            </Button>
            <Button onClick={handleCreateLocation}>Opprett</Button>
          </>
        }
      >
        <TextField
          label="Navn"
          value={newLocation.name}
          onChange={(e) =>
            setNewLocation({ ...newLocation, name: e.target.value })
          }
          placeholder="Skriv inn navn"
        />
        <div className={styles.pair}>
          <TextField
            label="Breddegrad"
            type="number"
            value={newLocation.lat}
            onChange={(e) =>
              setNewLocation({
                ...newLocation,
                lat: Number(e.target.value),
              })
            }
            placeholder="Skriv inn breddegrad"
          />
          <TextField
            label="Lengdegrad"
            type="number"
            value={newLocation.lon}
            onChange={(e) =>
              setNewLocation({
                ...newLocation,
                lon: Number(e.target.value),
              })
            }
            placeholder="Skriv inn lengdegrad"
          />
        </div>
        <div className={styles.pair}>
          <TextField
            label="Fylke"
            value={newLocation.county}
            onChange={(e) =>
              setNewLocation({ ...newLocation, county: e.target.value })
            }
            placeholder="Skriv inn fylke"
          />
          <TextField
            label="Kommune"
            value={newLocation.municipality}
            onChange={(e) =>
              setNewLocation({
                ...newLocation,
                municipality: e.target.value,
              })
            }
            placeholder="Skriv inn kommune"
          />
        </div>
        <Textarea
          label="Informasjon"
          rows={3}
          value={newLocation.information}
          onChange={(e) =>
            setNewLocation({
              ...newLocation,
              information: e.target.value,
            })
          }
          placeholder="Skriv inn informasjon"
        />
      </PageModal>

      <PageModal
        open={showCreatePrayerTableModal}
        title="Ny bønnetabell"
        icon="table"
        onClose={() => setShowCreatePrayerTableModal(false)}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowCreatePrayerTableModal(false)}
            >
              Avbryt
            </Button>
            <Button onClick={handleCreatePrayerTimeTable}>Opprett</Button>
          </>
        }
      >
        <TextField
          label="Navn på bønnetabell"
          value={newPrayerTableName}
          onChange={(e) => setNewPrayerTableName(e.target.value)}
          placeholder="Skriv inn navnet på bønnetabellen"
        />
      </PageModal>

      {/* Redigering av koordinater på en lokasjon (kun superadmin) */}
      {locationBeingEdited && (
        <PageModal
          open={showEditLocationModal}
          title={`Rediger koordinater – ${locationBeingEdited.name}`}
          icon="pin"
          onClose={closeEditLocationModal}
          footer={
            <>
              <Button variant="secondary" onClick={closeEditLocationModal}>
                Avbryt
              </Button>
              <Button onClick={handleSaveLocationEdits}>Lagre endringer</Button>
            </>
          }
        >
          <div className={styles.pair}>
            <TextField
              label="Breddegrad (lat)"
              type="number"
              value={editLat}
              onChange={(e) =>
                setEditLat(e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="Skriv inn breddegrad"
            />
            <TextField
              label="Lengdegrad (long)"
              type="number"
              value={editLon}
              onChange={(e) =>
                setEditLon(e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="Skriv inn lengdegrad"
            />
          </div>
        </PageModal>
      )}
    </>
  );
};

export default LocationsPage;
